import React, { useState } from 'react';
import { Receipt, Download, Eye, ArrowLeft } from 'lucide-react';
import { money } from './poShared';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const Sum = ({ label, value, testId }) => (
  <div className="border border-slate-200 rounded-sm px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-lg font-bold mono" data-testid={testId}>{value}</div></div>
);

const BillsPreview = ({ prev, f }) => (
  <div className="space-y-3 text-sm" data-testid="po-bills-preview-panel">
    <div className="grid grid-cols-4 gap-2">
      <Sum label="Bills" value={prev.count} testId="po-bills-count" /><Sum label="Before tax" value={money(prev.subtotal)} testId="po-bills-subtotal" />
      <Sum label="Tax" value={money(prev.tax)} testId="po-bills-tax" /><Sum label="Total" value={money(prev.total)} testId="po-bills-total" />
    </div>
    {prev.count === 0 ? <p className="text-center text-slate-500 py-6" data-testid="po-bills-empty">No received POs to bill between {f.date_from} and {f.date_to}.</p> : (
      <table className="w-full text-xs" data-testid="po-bills-table">
        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left border-b"><th className="py-1">Bill No</th><th>Vendor</th><th>Bill date</th><th>Due</th><th className="text-right">Lines</th><th className="text-right">Before tax</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
        <tbody>{prev.bills.map(b => (
          <tr key={b.bill_no} className="border-b border-slate-100" data-testid={`po-bill-row-${b.bill_no}`}>
            <td className="py-1.5 mono"><a href={`/purchasing/pos/${b.po_id}`} target="_blank" rel="noreferrer" className="hover:underline">{b.bill_no}</a></td><td>{b.vendor}</td><td>{b.bill_date}</td><td>{b.due_date}</td>
            <td className="text-right">{b.lines}</td><td className="text-right mono">{money(b.amount)}</td><td className="text-right mono">{money(b.tax)}</td><td className="text-right mono font-semibold">{money(b.total)}</td>
          </tr>
        ))}</tbody>
      </table>
    )}
    {!f.include_exported && prev.count > 0 && <p className="text-[11px] text-amber-700">Downloading marks these quantities as exported, so they won't appear in the next Bills export.</p>}
  </div>
);

export const PoBillsExportButton = ({ api }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ date_from: monthStart(), date_to: today(), include_exported: false, date_format: 'dd/mm/yyyy' });
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const close = (o) => { setOpen(o); if (!o) setPrev(null); };
  const preview = async () => {
    setBusy(true);
    try { const r = await api().post('/purchase-orders/export/quickbooks-bills/preview', f); setPrev(r.data); }
    catch (e) { toast.error('Preview failed'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await api().post('/purchase-orders/export/quickbooks-bills', f, { responseType: 'blob' });
      const n = Number(r.headers['x-bill-count'] || 0);
      if (!n) { toast.info('No received POs to bill in that range'); return; }
      const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `qbo-bills_${f.date_from}_${f.date_to}.csv`; a.click(); URL.revokeObjectURL(u);
      toast.success(`${n} bill${n > 1 ? 's' : ''} exported${f.include_exported ? '' : ' and marked as exported'}`);
      close(false);
    } catch (e) { toast.error('Bills export failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="rounded-sm" data-testid="po-bills-export-btn"><Receipt className="w-4 h-4 mr-1" /> Bills for QuickBooks</Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className={prev ? "max-w-4xl max-h-[90vh] overflow-y-auto" : "max-w-lg"} data-testid="po-bills-dialog">
          <DialogHeader><DialogTitle>Export Vendor Bills (QuickBooks Online)</DialogTitle></DialogHeader>
          {prev ? <BillsPreview prev={prev} f={f} /> : (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Received from</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="rounded-sm" data-testid="po-bills-from" /></div>
              <div><Label>Received to</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="rounded-sm" data-testid="po-bills-to" /></div>
            </div>
            <div><Label>Date format</Label>
              <select value={f.date_format} onChange={(e) => setF({ ...f, date_format: e.target.value })} className="w-full h-9 border border-slate-200 rounded-sm px-2 bg-white" data-testid="po-bills-date-format">
                <option value="dd/mm/yyyy">DD/MM/YYYY (QuickBooks Online Canada)</option>
                <option value="mm/dd/yyyy">MM/DD/YYYY (QuickBooks Online US)</option>
                <option value="yyyy-mm-dd">YYYY-MM-DD</option>
              </select>
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.include_exported} onChange={(e) => setF({ ...f, include_exported: e.target.checked })} data-testid="po-bills-include-exported" /> Include already exported (re-export full received amounts, doesn't mark)</label>
            <p className="text-[11px] text-slate-500">One bill per Received / Partially Received / Closed PO: Bill No = PO number, Bill Date = last receive date, Due Date from vendor terms. Only quantities received and not yet billed are included. A later partial receipt becomes bill <span className="mono">PO#-2</span>. Freight is billed once. Tax codes and accounts come from Admin → Settings → Purchase Orders.</p>
          </div>
          )}
          <DialogFooter>
            {prev ? <Button variant="ghost" onClick={() => setPrev(null)} className="rounded-sm mr-auto" data-testid="po-bills-back"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Change filters</Button>
              : <Button variant="outline" onClick={() => close(false)} className="rounded-sm" data-testid="po-bills-cancel">Cancel</Button>}
            {!prev && <Button onClick={preview} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-bills-preview"><Eye className="w-3.5 h-3.5 mr-1" />{busy ? 'Loading…' : 'Preview bills'}</Button>}
            {prev && prev.count > 0 && <Button onClick={download} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-bills-download"><Download className="w-3.5 h-3.5 mr-1" />{busy ? 'Exporting…' : f.include_exported ? 'Download CSV' : 'Download & mark exported'}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PoBillsExportButton;
